<template>
  <section class="costs-breakdown" :class="{ desktop: $isDesktop }">
    <section class="cost-section your-booking">
      <table>
        <tbody>
          <tr>
            <td colspan="2">
              <translate class="amount" tag="h4"> Your booking </translate>
            </td>
          </tr>
          <tr>
            <td colspan="2">
              <div
                v-show="
                  chargetype.state === 'unavailable' &&
                  !chargetype.isOvercapacityMessage &&
                  alternativeStays &&
                  !alternativeStays.loading.value &&
                  alternativeStays.data.value.length
                "
                class="availability-error-state"
              >
                <img
                  loading="lazy"
                  src="@/assets/images/warning-red.svg"
                  width="30"
                  height="30"
                  data-test="warning-red-3"
                /><translate> No availability on our site </translate>
              </div>

              <div class="dates-summary-cell">
                <div
                  class="dates-summary"
                  :class="{
                    'search-error':
                      chargetype.state === 'unavailable' &&
                      !chargetype.isOvercapacityMessage,
                  }"
                  @click.prevent="$emit('check-availability')"
                >
                  <pu-utc-date :date="start" options="noYear" class="start" />
                  -
                  <translate
                    v-if="onlyArriveSelected"
                    class="choose-date-link"
                    @click="$emit('check-availability')"
                  >
                    Choose depart date
                  </translate>
                  <template v-else>
                    <pu-utc-date :date="end" options="noYear" class="end" />
                    <pu-utc-date :date="end" options="year" class="year" />
                    <div
                      v-if="!onlyArriveSelected"
                      class="nights"
                      :class="{
                        'search-error': chargetype.state === 'min_nights',
                      }"
                    >
                      <translate :tt="{ nights: dateRangeNights }">
                        {nights, plural, one{# night} other{# nights}}
                      </translate>
                    </div>
                  </template>
                  <a
                    v-if="!onlyArriveSelected"
                    class="change-btn"
                    @click.prevent.stop="$emit('check-availability')"
                  >
                    <translate>Change</translate>
                  </a>
                </div>
                <div class="dates-clear-btn" @click="clearDates">
                  <img src="@/assets/images/cross-circle-grey.svg" />
                </div>
              </div>
            </td>
          </tr>

          <tr
            v-show="
              chargetype.state === 'unavailable' &&
              !chargetype.isOvercapacityMessage &&
              alternativeStays &&
              !alternativeStays.loading.value &&
              alternativeStays.data.value.length
            "
            class="alternative-dates"
          >
            <td colspan="2">
              <pitchtype-alternative-stays />
            </td>
          </tr>

          <tr v-if="!onlyArriveSelected && chargeTypesUpdating">
            <td colspan="2">
              <loading-spinner />
              <translate>Loading...</translate>
            </td>
          </tr>
          <tr
            v-if="
              chargetype &&
              (chargetype.state === 'max_capacity' ||
                chargetype.state === 'adults_only' ||
                chargetype.state === 'unavailable')
            "
          >
            <td
              colspan="2"
              class="max-capacity-msg"
              :class="{
                'unavailable-chargetype':
                  chargetype.state === 'unavailable' &&
                  !chargetype.isOvercapacityMessage,
                'max-capacity-msg-dogs': hasDogs,
              }"
            >
              <span v-if="hasDogs" class="search-error">
                <translate :tt="{ adults: party.adults }">
                  {adults} adults </translate
                ><span>, </span
                ><translate v-if="hasChildren" :tt="{ childrenCount }">
                  {childrenCount} children </translate
                ><span v-if="hasChildren">, </span
                ><translate :tt="{ dogs }"> {dogs} dogs </translate>
              </span>
              <translate
                v-else-if="hasChildren"
                :tt="{ adults: party.adults, childrenCount }"
                class="search-error"
              >
                {adults} adults, {childrenCount} children
              </translate>
              <translate
                v-else
                class="search-error"
                :tt="{ adults: party.adults }"
              >
                {adults} adults
              </translate>
              <party-edit
                :pitchtype="pitchtype"
                :party="party"
                :pitchtype-capacity="pitchtype.capacity"
                :chargetype-state="chargetype.state"
                :persons-included="
                  pitchtype.chargetype.personsIncluded ??
                  pitchtype.personsIncluded
                "
                :capacity-display="pitchtype.capacityDisplay"
                :show-party-details="false"
              />
              <capacity-rules
                v-if="chargetype.isOvercapacityMessage"
                :age-restrictions="ageRestrictions"
              />
            </td>
          </tr>
          <tr v-if="chargetypeAvailable" class="title-row">
            <td colspan="2">
              <translate class="section-title"> Price </translate>
            </td>
          </tr>
          <tr v-if="chargetypeAvailable && hasBasePriceAmount">
            <td>
              <!-- Use charge type personsIncluded (if available) with fallback to pitch type value for backward compatibility -->
              <template
                v-if="
                  pitchtype.chargetype.personsIncluded ??
                  pitchtype.personsIncluded
                "
              >
                <translate
                  v-if="
                    (pitchtype.chargetype.personsIncluded ??
                      pitchtype.personsIncluded) == -1
                  "
                >
                  Unlimited people
                </translate>
                <translate
                  v-else
                  :tt="{
                    personsIncluded:
                      pitchtype.chargetype.personsIncluded ??
                      pitchtype.personsIncluded,
                  }"
                >
                  Up to {personsIncluded} people
                </translate>
                <br />
              </template>
              <translate v-else> Basic price (inc. 0 persons) </translate>
            </td>
            <td class="right">
              <pu-price
                :price="preDiscountBasePrice"
                :display-original-price="true"
              />
              <translate class="per-stay"> per stay </translate>
            </td>
          </tr>
        </tbody>
      </table>

      <persons
        v-if="chargetypeAvailable && adults"
        :persons="adults"
        :persons-price="preDiscountPriceAdults"
        :pricing-method="pricingMethod"
        :persons-included="
          pitchtype.chargetype.personsIncluded ?? pitchtype.personsIncluded
        "
        :pitchtype-capacity="pitchtype.capacity"
        :party-size="partySize"
        :campsite-slug="campsite.slug"
        :pitchtype-id="chargetype.pitchtypeId"
      >
        <translate :tt="{ people: adults }">
          {people, plural, one{# adult} other{# adults}}
        </translate>
      </persons>

      <persons
        v-if="chargetypeAvailable && children"
        :persons="children"
        :persons-price="preDiscountPriceChildren"
        :pricing-method="pricingMethod"
        :persons-included="
          pitchtype.chargetype.personsIncluded ?? pitchtype.personsIncluded
        "
      >
        <translate :tt="{ childrenCount: children }">
          {childrenCount, plural, one{# child} other{# children}}
        </translate>
      </persons>

      <persons
        v-if="chargetypeAvailable && infants"
        :persons="infants"
        :persons-price="preDiscountPriceInfants"
        :pricing-method="pricingMethod"
        :persons-included="
          pitchtype.chargetype.personsIncluded ?? pitchtype.personsIncluded
        "
      >
        <translate :tt="{ infantsCount: infants }">
          {infantsCount, plural, one{# infant} other{# infants}}
        </translate>
      </persons>

      <persons
        v-for="(appliedExtra, index) in appliedExtras"
        :key="index"
        :persons-price="mapAppliedExtraPriceToPersonPrice(appliedExtra.price)"
      >
        {{ `${appliedExtra.quantity} ${appliedExtra.name}` }}
      </persons>

      <persons
        v-if="!hasAppliedExtras && hasDogs"
        :persons="dogs"
        :persons-price="emptyPrice"
      >
        <translate :tt="{ dogs }"> {dogs} dogs </translate>
      </persons>

      <party-edit
        v-if="
          chargetypeAvailable &&
          chargetype.state !== 'max_capacity' &&
          chargetype.state !== 'min_nights' &&
          chargetype.state !== 'adults_only' &&
          chargetype.state !== 'unavailable'
        "
        :pitchtype="pitchtype"
        :party="party"
        :pitchtype-capacity="pitchtype.capacity"
        :persons-included="
          pitchtype.chargetype.personsIncluded ?? pitchtype.personsIncluded
        "
        :capacity-display="pitchtype.capacityDisplay"
      />

      <pitchtypes-compulsory-extras
        v-if="chargetypeAvailable && compulsoryExtras.length"
        :extras="compulsoryExtras"
        class="costs-breakdown-table"
      />

      <taxes
        v-if="chargetypeAvailable && chargetype.taxes.length"
        :taxes="chargetype.taxes"
        class="costs-breakdown-table"
      />

      <offers-active
        v-if="chargetypeAvailable && offersActive"
        :pre-discount-price="preDiscountPrice"
        :offers-applied="offersApplied"
        :final-price="price"
      />

      <total-stay
        v-if="chargetypeAvailable"
        :price="price"
        :offers-active="offersActive"
        :is-immediate-payment="isImmediatePayment"
      />

      <arrival-tax
        v-if="chargetypeAvailable && chargetype.arrivalTaxes"
        :taxes="chargetype.arrivalTaxes"
      />
    </section>

    <stripe-payment
      v-if="chargetypeAvailable"
      :campsite-name="campsite.name"
      :stripe-payment-when="stripePaymentWhen"
      :deposit="chargetype.deposit"
      :remainder="chargetype.remainder"
      :campsite-payment-timing="campsite.paymentTiming"
      :price="price"
      :balance-due-date="chargetype.balanceDueDate"
      :refund-until-date="chargetype.refundUntilDate"
      :campsite-refund-policy="campsite.refundPolicy"
    />

    <booking-balance
      v-if="chargetypeAvailable"
      :balance-due-date="chargetype.balanceDueDate"
      :campsite-allow-override-immediate="campsite.allowOverrideImmediate"
      :campsite-payment-timing="campsite.paymentTiming"
      :campsite-refund-policy="campsite.refundPolicy"
      :refund-until-date="chargetype.refundUntilDate"
      :stripe-payment-when="stripePaymentWhen"
      class="costs-breakdown-note"
    />

    <cancellation-policy-popover
      :campsite-name="campsite.name"
      :campsite-cancellation-policy="campsite.cancellationPolicy"
    />

    <p v-if="cityTax" class="prerendered">
      {{ cityTax }}
    </p>

    <div
      v-if="chargetypeAvailable && campsite.usefulInfo"
      v-thtml="campsite.usefulInfo"
      class="prerendered"
    />

    <template v-if="chargetypeAvailable && campsite.termsUsefulInfo">
      <template v-for="(info, index) in campsite.termsUsefulInfo" :key="index">
        <div v-thtml="info" class="prerendered" />
      </template>
    </template>
  </section>
</template>

<script lang="ts">
import { ComputedRef } from 'vue'
import { DateRange } from '~/utility/date/DateRange'
import {
  Component,
  Inject,
  Prop,
  Vue,
  toNative,
} from '~/utility/pu-class-decorator'
import { CalendarDay } from '../../../campsite-details/alternativeStaysFetch'
import type {
  AgeRestrictions,
  CampsiteCityTax,
  ChargeType,
  CostsBreakdownCampsiteDetails,
  CostsBreakdownPitchtypeDetails,
  PitchTypeExtra,
} from '../../../campsite-details/types'
import type { Party } from '../../types'

@Component({
  emits: ['clearDates', 'check-availability'],
  setup: () => {
    return {
      currencyStore: useCurrencyStore(usePinia()),
    }
  },
})
class CostsBreakdown extends Vue {
  currencyStore: ReturnType<typeof useCurrencyStore>

  @Prop({ required: true })
  campsite: CostsBreakdownCampsiteDetails

  @Prop({ required: true })
  pitchtype: CostsBreakdownPitchtypeDetails

  @Prop()
  ageRestrictions: AgeRestrictions

  @Prop({ required: true })
  start: string

  @Prop({ required: true })
  end: string

  @Prop({ required: false })
  cityTax: CampsiteCityTax

  @Prop({ required: true })
  chargeTypesUpdating: boolean

  @Prop({ required: true })
  party: Party

  @Prop()
  pricingMethod: number

  @Inject({ from: 'alternativeStays', default: undefined }) alternativeStays?: {
    data: ComputedRef<CalendarDay[]>
    loading: ComputedRef<boolean>
    error: ComputedRef<Error | null>
  }

  get onlyArriveSelected(): boolean {
    return this.end ? false : Boolean(this.start)
  }

  get chargetype(): ChargeType {
    return this.pitchtype && this.pitchtype.chargetype
  }

  get appliedExtras() {
    return this.chargetype?.appliedExtras
  }

  get hasAppliedExtras() {
    return this.appliedExtras && this.appliedExtras.length > 0
  }

  get stripePaymentWhen() {
    return this.chargetype && this.chargetype.stripePaymentWhen
  }

  get chargetypeAvailable() {
    return (
      !this.onlyArriveSelected &&
      this.chargetype &&
      this.chargetype.state === 'available' &&
      !this.chargeTypesUpdating
    )
  }

  get compulsoryExtras(): PitchTypeExtra[] {
    if (this.chargetype) {
      return this.chargetype.extras.filter((extra) => extra.compulsory)
    }
    return this.pitchtype.extras.filter((extra) => extra.compulsory)
  }

  get hasBasePriceAmount() {
    return (
      this.chargetype.quote?.basePrice?.amount &&
      this.chargetype.quote?.basePrice?.amount > 0
    )
  }

  get preDiscountBasePrice() {
    return this.chargetype.quote!.preDiscountBasePrice
  }

  get isImmediatePayment() {
    return this.stripePaymentWhen === 'immediate_payment'
  }

  get childrenCount() {
    return this.party ? this.party.childAges.length : 0
  }

  get dogs() {
    return this.party?.dogs || 0
  }

  get hasChildren(): boolean {
    return this.childrenCount > 0
  }

  get hasDogs(): boolean {
    return this.dogs > 0
  }

  get partySize() {
    return this.party.adults + this.party.childAges.length
  }

  get adults() {
    return this.chargetype.quote?.adults || 0
  }

  get children() {
    return this.chargetype.quote?.children || 0
  }

  get infants() {
    return this.chargetype.quote?.infants || 0
  }

  get preDiscountPriceAdults() {
    return this.chargetype.quote!.preDiscountPriceAdults
  }

  get preDiscountPriceChildren() {
    return this.chargetype.quote!.preDiscountPriceChildren
  }

  get preDiscountPriceInfants() {
    return this.chargetype.quote!.preDiscountPriceInfants
  }

  get offersActive() {
    return this.chargetype?.quote?.offersActive
  }

  get preDiscountPrice() {
    return this.chargetype.quote!.preDiscountPrice
  }

  get offersApplied() {
    return this.chargetype.quote!.offersApplied
  }

  get dateRangeNights() {
    const start = this.start
    const end = this.end
    if (start && end) {
      return new DateRange(start, end).nights
    }
    return 1
  }

  get price() {
    return this.chargetype.priceExArrivalTaxes || this.chargetype.price
  }

  get emptyPrice() {
    return {
      amountMinor: 0,
      amountMajor: 0,
      currency: this.currencyStore.currentCurrencyCode,
    }
  }

  mapAppliedExtraPriceToPersonPrice(appliedExtraPrice) {
    // appliedExtras have amount_minor instead of amountMinor
    return {
      ...appliedExtraPrice,
      amountMinor: appliedExtraPrice.amount_minor,
    }
  }

  clearDates() {
    this.$bubble('clearDates')
    this.$emit('check-availability')
  }
}

export default toNative(CostsBreakdown)
</script>

<style>
@import url('~/assets/css/costs-breakdown.css');
</style>
